import React from 'react';
/**
 * `RegistrationInput` is a specialised text input for vehicle registration
 * numbers.
 *
 * Behaviour:
 * - Displays a GB or EU flag prefix depending on `appConfig.countryCode`.
 * - Filters characters on input using `regexp` (defaults to
 *   `ALPHANUMERIC_REGEXP`).
 * - Trims whitespace on blur.
 * - Supports readonly rendering.
 */
import ukflag from '../../images/ukflag.png';
import './RegistrationInput.scss';
import euflag from '../../images/euflag.png';
import { CountryIsoCodes } from './Constants';
import { useAppSelector } from '../../app-state/Hooks';

export interface InputLabelProps {
  name: string;
  labelText: string;
  className?: string;
}

const InputLabel = ({ name, labelText, className = '' }: InputLabelProps) => {
  return (
    <label className={className} aria-label={labelText} htmlFor={name}>
      {labelText}
    </label>
  );
};

export interface TextInputProps {
  name: string;
  className?: string;
  ariaLabel?: string;
  error?: string;
  hasError?: boolean;
  placeholder?: string;
  label?: string;
  value?: string;
  dataKey?: string;
  maxLength?: number;
  readOnly?: boolean;
  hasLabel?: boolean;
  autoFocus?: boolean;
  focus?: boolean;
  handleReturn?: boolean;
  stopPropagationOnClick?: boolean;
  onFocus?: (event?: React.FormEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
  onLeave?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
}

const RegistrationInput = (props: TextInputProps): JSX.Element => {
  const {
    name,
    placeholder,
    ariaLabel,
    value,
    dataKey = '',
    maxLength = 10000,
    className,
    stopPropagationOnClick,
    handleReturn,
    autoFocus,
    hasLabel,
    label,
    error,
    hasError,
    readOnly,
    focus,
    onChange,
    onLeave,
    onKeyPress,
    onFocus
  } = props;

  const countryCode = useAppSelector((s) => s.appConfig.countryCode);
  const vregClassName =
    countryCode == CountryIsoCodes.GB ? 'form-control' : 'form-control inputeu';

  const onChangeInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (onChange !== undefined) {
      const value = event.currentTarget.value
        ? event.currentTarget.value.replace(/[^0-9a-zA-Z]+/gi, '')
        : '';

      if (event.currentTarget.value !== value) {
        event.currentTarget.value = value;
      }
      onChange(event, dataKey);
    }
  };

  // add a handler to trim the values on leave ...
  const onLeaveInput = (event: React.FormEvent<HTMLInputElement>) => {
    const val = event.currentTarget.value;

    if (val !== undefined) {
      event.currentTarget.value = val.trim();

      onChangeInput(event);
    }

    if (onLeave !== undefined) {
      onLeave(event, dataKey);
    }
  };

  const onClickInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (stopPropagationOnClick) {
      event.stopPropagation();
    }
  };

  const onKeyPressed = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (handleReturn) {
      if (event.key == 'Enter') {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const blurEvent: any = { currentTarget: { value: null } };
        // eslint-disable-next-line  @typescript-eslint/no-unsafe-argument
        onLeaveInput(blurEvent);
      }
    }

    if (onKeyPress !== undefined) {
      onKeyPress(event);
    }
  };

  let styleWrapper = 'form-group registration-control';
  if (error !== undefined && error.length > 0) {
    styleWrapper += ' has-error';
  }
  if (hasError === true) {
    styleWrapper += ' has-error';
  }

  let baseClasses = 'field ';
  if (className) {
    baseClasses += className;
  }

  const createLabel = (): JSX.Element => {
    if (hasLabel && label !== undefined) {
      return <InputLabel labelText={label} name={name} />;
    }
    return <></>;
  };

  const createRenderPanel = (): JSX.Element => {
    if (readOnly) {
      return (
        <input
          id={name}
          name={name}
          data-testid={name}
          aria-label={ariaLabel}
          type='text'
          autoComplete='off'
          className={vregClassName}
          placeholder={placeholder || label}
          value={value}
          readOnly
          disabled
          ref={(input) => input && focus && input.focus()}
        />
      );
    } else {
      return (
        <input
          id={name}
          name={name}
          data-testid={name}
          aria-label={ariaLabel}
          type='text'
          autoComplete='off'
          className={vregClassName}
          placeholder={placeholder || label}
          autoFocus={autoFocus}
          value={value}
          onKeyPress={onKeyPressed}
          onChange={onChangeInput}
          onFocus={onFocus}
          onBlur={onLeaveInput}
          onClick={onClickInput}
          maxLength={maxLength}
          ref={(input) => input && focus && input.focus()}
        />
      );
    }
  };

  const labelControl = createLabel();
  const inputControl = createRenderPanel();

  return (
    <div className={styleWrapper}>
      {labelControl}
      <div className={baseClasses}>
        <div className='input-group'>
          <div className='prepend'>
            {countryCode === CountryIsoCodes.GB && (
              <img src={ukflag} alt='uk flag' />
            )}
            {countryCode !== CountryIsoCodes.GB && (
              <img src={euflag} alt='eu flag' />
            )}
          </div>
          {inputControl}
        </div>
      </div>
      <div className='input'>{error}</div>
    </div>
  );
};

export default RegistrationInput;
